import { type ComponentProps, useCallback } from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';

/**
 * ResponsiveDialog - Adaptive Dialog/Drawer component
 *
 * Automatically switches between Dialog (desktop) and Drawer (mobile)
 * based on 'md' breakpoint (768px).
 *
 * @example
 * ```tsx
 * <ResponsiveDialog open={open} onOpenChange={setOpen}>
 *   <ResponsiveDialogContent>
 *     <ResponsiveDialogHeader>
 *       <ResponsiveDialogTitle>Title</ResponsiveDialogTitle>
 *     </ResponsiveDialogHeader>
 *     <ResponsiveDialogFooter>
 *       <Button>Action</Button>
 *     </ResponsiveDialogFooter>
 *   </ResponsiveDialogContent>
 * </ResponsiveDialog>
 * ```
 *
 * @warning
 * ⚠️ Use only for simple modals with similar behavior on desktop/mobile.
 *
 * DO NOT use if you need:
 * - Fundamentally different layouts for desktop/mobile
 * - Drawer-specific props (direction, snapPoints, etc.)
 * - Complex Dialog positioning logic
 * - Different animations or behavior
 *
 * In such cases, use separate Dialog and Drawer components
 * with conditional rendering via useBreakpoint.
 *
 * Always test on both desktop and mobile!
 */

export type ResponsiveDialogProps = ComponentProps<typeof Dialog> &
  ComponentProps<typeof Drawer>;

function ResponsiveDialog({
  children,
  onOpenChange,
  onAnimationEnd,
  ...props
}: ResponsiveDialogProps) {
  const isDesktop = useBreakpoint('md');

  // On desktop, Dialog doesn't support onAnimationEnd, so we call it
  // immediately when the dialog closes via onOpenChange
  const handleDesktopOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        onAnimationEnd?.(open);
      }
    },
    [onOpenChange, onAnimationEnd],
  );

  if (isDesktop) {
    return (
      <Dialog onOpenChange={handleDesktopOpenChange} {...props}>
        {children}
      </Dialog>
    );
  }

  return (
    <Drawer
      onOpenChange={onOpenChange}
      onAnimationEnd={onAnimationEnd}
      {...props}
    >
      {children}
    </Drawer>
  );
}

export type ResponsiveDialogContentProps = ComponentProps<
  typeof DialogContent
> &
  ComponentProps<typeof DrawerContent>;

function ResponsiveDialogContent({
  children,
  className,
  ...props
}: ResponsiveDialogContentProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return (
      <DialogContent className={className} {...props}>
        {children}
      </DialogContent>
    );
  }

  return (
    <DrawerContent className={className} {...props}>
      {children}
    </DrawerContent>
  );
}

export type ResponsiveDialogHeaderProps = ComponentProps<typeof DialogHeader> &
  ComponentProps<typeof DrawerHeader>;

function ResponsiveDialogHeader({
  children,
  className,
  ...props
}: ResponsiveDialogHeaderProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return (
      <DialogHeader className={className} {...props}>
        {children}
      </DialogHeader>
    );
  }

  return (
    <DrawerHeader className={className} {...props}>
      {children}
    </DrawerHeader>
  );
}

export type ResponsiveDialogTitleProps = ComponentProps<typeof DialogTitle> &
  ComponentProps<typeof DrawerTitle>;

function ResponsiveDialogTitle({
  children,
  className,
  ...props
}: ResponsiveDialogTitleProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return (
      <DialogTitle className={className} {...props}>
        {children}
      </DialogTitle>
    );
  }

  return (
    <DrawerTitle className={className} {...props}>
      {children}
    </DrawerTitle>
  );
}

export type ResponsiveDialogDescriptionProps = ComponentProps<
  typeof DialogDescription
> &
  ComponentProps<typeof DrawerDescription>;

function ResponsiveDialogDescription({
  children,
  className,
  ...props
}: ResponsiveDialogDescriptionProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return (
      <DialogDescription className={className} {...props}>
        {children}
      </DialogDescription>
    );
  }

  return (
    <DrawerDescription className={className} {...props}>
      {children}
    </DrawerDescription>
  );
}

export type ResponsiveDialogFooterProps = ComponentProps<typeof DialogFooter> &
  ComponentProps<typeof DrawerFooter>;

function ResponsiveDialogFooter({
  children,
  className,
  ...props
}: ResponsiveDialogFooterProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return (
      <DialogFooter className={className} {...props}>
        {children}
      </DialogFooter>
    );
  }

  return (
    <DrawerFooter className={className} {...props}>
      {children}
    </DrawerFooter>
  );
}

export type ResponsiveDialogCloseProps = ComponentProps<typeof DialogClose> &
  ComponentProps<typeof DrawerClose>;

function ResponsiveDialogClose({
  children,
  ...props
}: ResponsiveDialogCloseProps) {
  const isDesktop = useBreakpoint('md');

  if (isDesktop) {
    return <DialogClose {...props}>{children}</DialogClose>;
  }

  return <DrawerClose {...props}>{children}</DrawerClose>;
}

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
};
